<template>
  <form @submit.prevent="submit">
    <p>{{ $t("global.safe-harbor-migration-help-description") }}</p>
    <FormTextArea
      name="description"
      v-model="fields.description"
      testId="description-input"
      :label="`${$t('global.description')} *`"
      :errors="validation.fields.description.errors"
      :dirty="validation.fields.description.dirty"
      :disabled="isSubmitting"
      class="mb-4"
    />
    <FormSelect
      name="budget"
      v-model="fields.budget"
      testId="budget-input"
      :label="`${$t('global.budget')} *`"
      :errors="validation.fields.budget.errors"
      :dirty="validation.fields.budget.dirty"
      :disabled="isSubmitting"
    >
      <option v-for="(option, index) in getBudgets()" :key="index" :value="option">
        {{ option }}
      </option>
    </FormSelect>
    <div class="form-buttons">
      <Btn
        class="mr-2"
        variant="light"
        type="button"
        :disabled="isSubmitting"
        @click="cancel"
        data-test="cancel-request-safe-harbor-migration-help-button"
      >
        {{ $t("global.cancel") }}
      </Btn>
      <Btn
        variant="primary"
        type="submit"
        :disabled="!isSubmittable()"
        :loading="isSubmitting"
        data-test="request-safe-harbor-migration-help-button"
      >
        {{ $t("global.request-help") }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import FormSelect from "@/components/display/form/FormSelect";
import FormTextArea from "@/components/display/form/FormTextArea";
import store from "@/store";
import useValidator from "@/utils/useValidator";
import { requestHelp as validationRules } from "leatherman-js/utils/validations/forms";

export default {
  name: "SafeHarborMigrationHelpForm",
  props: {
    resourceId: {
      type: Number,
      required: true,
    },
    type: {
      type: String,
      require: true,
      validator: (value) => ["shared", "site"].includes(value),
    },
  },
  components: {
    FormSelect,
    FormTextArea,
    Btn,
  },
  setup(props, { emit, root }) {
    const isSubmitting = ref(false);
    const errors = ref(null);
    const fields = reactive({ budget: null, description: null });
    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules,
      {},
      {
        budget: {
          required: root.$t("validation.budget-required"),
        },
        description: {
          required: root.$t("validation.description-required"),
        },
      }
    );
    const cancel = () => emit("close");

    const getBudgets = () => [
      "$0 to $9,995",
      "$10,000 to $49,995",
      "$50,000 to $99,995",
      "$100,000+",
    ];

    const isSubmittable = () => !isSubmitting.value && validation.errors.length === 0;
    const submit = () => {
      errors.value = null;

      validation
        .validate()
        .then(() => {
          isSubmitting.value = true;
          const action =
            props.type === "site"
              ? "safeHarbor/createSiteSafeHarborMigrationLead"
              : "safeHarbor/createSharedHostSafeHarborMigrationLead";
          store
            .dispatch(action, {
              id: props.resourceId,
              data: {
                budget: fields.budget,
                description: fields.description,
              },
            })
            .then(() => {
              store.dispatch("toasts/addToast", {
                variant: "info",
                message: root.$t("toasts.safe-harbor-help-request-success"),
              });
              emit("close");
            })
            .catch(() => {
              store.dispatch("toasts/addToast", {
                variant: "error",
                message: root.$t("toasts.safe-harbor-help-request-error-message", {
                  name: props.planName,
                }),
              });
            })
            .finally(() => {
              isSubmitting.value = false;
            });
        })
        .catch(() => {});
    };

    return {
      cancel,
      errors,
      fields,
      getBudgets,
      isSubmittable,
      isSubmitting,
      submit,
      validation,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/variables";
.form-buttons {
  text-align: right;
  padding-top: 2rem;
}
</style>
